import { createStandardCachedResolver } from 'reference-resolvers-lite/utils/createStandardCachedResolver';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useStrategy } from '../../hooks/useStrategy';
import { useViewerActions } from '../../hooks/useViewerActions';
import { LogicGroupNames, getLogicGroupConditionsSize, isLogicGroupWithFilterValidationErrors, isLogicGroupWithIncompleteFilters } from '../../filterQueryFormat/logic/LogicGroup';
import { referenceToOption as defaultReferenceToOption } from 'reference-resolvers-lite/utils/referenceToOption';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { PASS } from 'customer-data-filters-ui/filterQueryFormat/MatchStatus';
import styled from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ReferenceLiteSearchSelect from 'reference-resolvers-lite/components/ReferenceLiteSearchSelect';
import SegmentationFiltersInstanceContext from '../../state/SegmentationFiltersInstanceContext';
import RollupTestFiltersStatusMessage from './RollupTestFiltersStatusMessage';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { LoadingDots } from 'foundations-components/transitional/components/LoadingDots';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledReferenceLiteSearchSelectFail = styled(ReferenceLiteSearchSelect).withConfig({
  displayName: "TestFilters__StyledReferenceLiteSearchSelectFail",
  componentId: "hsq37h-0"
})(["border:1px solid #f2545b !important;background-color:#fdedee !important;"]);
const StyledReferenceLiteSearchSelectPass = styled(ReferenceLiteSearchSelect).withConfig({
  displayName: "TestFilters__StyledReferenceLiteSearchSelectPass",
  componentId: "hsq37h-1"
})(["border:1px solid #00bda5 !important;background-color:#e5f8f6 !important;"]);
export const contactFormatter = reference => {
  const option = defaultReferenceToOption(reference);
  const email = reference.additionalProperties.properties.email;
  return Object.assign({}, option, {
    text: reference.label || email,
    help: email
  });
};
export const getOptionFormatter = objectTypeId => {
  if (objectTypeId === CONTACT_TYPE_ID) {
    return contactFormatter;
  }
  return undefined;
};
const TestFilters = () => {
  const {
    internalDraftValue,
    testFiltersStatus,
    testFiltersSelectedObjectId,
    getIsUngated,
    props: {
      baseFilterFamily,
      strategy
    }
  } = useContext(SegmentationFiltersInstanceContext);
  const {
    getFilterFamilyObjectName,
    evaluateFiltersForObject
  } = useStrategy();
  const {
    onTestFiltersObjectChange,
    onTestFiltersUpdate
  } = useViewerActions();
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toObjectSeg = useCallback(internal => {
    const hasFilters = !!getLogicGroupConditionsSize(internal);
    if (!hasFilters) {
      return undefined;
    }
    const hasIncompleteFilters = hasFilters && isLogicGroupWithIncompleteFilters(internal, getIsUngated);
    const hasFilterValidationErrors = hasFilters && isLogicGroupWithFilterValidationErrors(internal);
    if (!hasIncompleteFilters) {
      const filterQuery = strategy.serializer.toFormat(internal, getIsUngated, {
        useHoursFiltering: strategy.useHoursFiltering
      });
      if (!hasFilterValidationErrors) {
        return filterQuery;
      }
    }
    return undefined;
  }, [getIsUngated, strategy.serializer, strategy.useHoursFiltering]);
  const resolver = createStandardCachedResolver('CRM_OBJECT', baseFilterFamily);
  const objectType = getFilterFamilyObjectName(baseFilterFamily);
  const lowercaseObjectType = objectType.toLocaleLowerCase();
  const onSelectedObjectChange = evt => {
    onTestFiltersObjectChange(evt.target.value);
  };
  useEffect(() => {
    let isStaleRequest = false;
    const external = internalDraftValue && toObjectSeg(internalDraftValue);
    if (external && baseFilterFamily && testFiltersSelectedObjectId) {
      setHasError(false);
      setIsLoading(true);
      evaluateFiltersForObject === null || evaluateFiltersForObject === void 0 || evaluateFiltersForObject(internalDraftValue, external, baseFilterFamily, testFiltersSelectedObjectId).then(value => {
        if (!isStaleRequest) {
          onTestFiltersUpdate(value);
        }
        setIsLoading(false);
      }).catch(() => {
        onTestFiltersUpdate(undefined);
        setHasError(true);
        setIsLoading(false);
      });
    } else {
      onTestFiltersUpdate(undefined);
    }
    return () => {
      isStaleRequest = true;
      setIsLoading(true);
      setHasError(false);
    };
  }, [toObjectSeg, baseFilterFamily, internalDraftValue, evaluateFiltersForObject, testFiltersSelectedObjectId, onTestFiltersUpdate, setHasError]);
  const filtersStatus = testFiltersStatus === null || testFiltersStatus === void 0 ? void 0 : testFiltersStatus.filtersStatus;
  const recordStatus = (testFiltersStatus === null || testFiltersStatus === void 0 ? void 0 : testFiltersStatus.recordStatus) || filtersStatus;
  let ReferenceLiteSearchSelectVariant = ReferenceLiteSearchSelect;
  if (recordStatus) {
    ReferenceLiteSearchSelectVariant = recordStatus === PASS ? StyledReferenceLiteSearchSelectPass : StyledReferenceLiteSearchSelectFail;
  }
  const rollupStatus = useMemo(() => {
    if (filtersStatus === PASS) {
      if ((internalDraftValue === null || internalDraftValue === void 0 ? void 0 : internalDraftValue.logicGroupName) === LogicGroupNames.AND) {
        return 'successAnd';
      }
      return 'successOr';
    }
    if ((internalDraftValue === null || internalDraftValue === void 0 ? void 0 : internalDraftValue.logicGroupName) === LogicGroupNames.AND) {
      return 'failureAnd';
    }
    return 'failureOr';
  }, [filtersStatus, internalDraftValue === null || internalDraftValue === void 0 ? void 0 : internalDraftValue.logicGroupName]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("small", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.testFilters.selectARecord",
        options: {
          lowercaseObjectType,
          objectType
        }
      })
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(ReferenceLiteSearchSelectVariant, {
      referenceToOption: getOptionFormatter(baseFilterFamily),
      filterOptions: false,
      onChange: onSelectedObjectChange,
      resolver: resolver,
      value: testFiltersSelectedObjectId
    }), isLoading && /*#__PURE__*/_jsx(LoadingDots, {
      paddingBlockStart: "400"
    }), (testFiltersStatus === null || testFiltersStatus === void 0 ? void 0 : testFiltersStatus.additionalMatchMessage) || null, filtersStatus && /*#__PURE__*/_jsx(RollupTestFiltersStatusMessage, {
      use: rollupStatus
    }), hasError && /*#__PURE__*/_jsx(Alert, {
      closeable: true,
      onClose: () => setHasError(false),
      type: "danger",
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.testFilters.error.header"
      }),
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "customerDataFiltersUI.testFilters.error.message"
      })
    })]
  });
};
export default TestFilters;